import { PDFDocument } from 'pdf-lib';

export interface MergeProgress {
  percent: number;
  phase: 'loading' | 'merging' | 'saving';
}

export type MergeProgressCallback = (progress: MergeProgress) => void;

/**
 * Merges an array of PDF File objects into a single PDF File.
 * Reports progress via callback: 80% for loading+copying, 20% for save.
 */
export async function mergePdfs(
  files: File[],
  outputFilename: string,
  onProgress?: MergeProgressCallback,
): Promise<File> {
  if (files.length === 0) {
    throw new Error('No files to merge');
  }

  if (files.length === 1) {
    onProgress?.({ percent: 100, phase: 'saving' });
    return new File([files[0]], outputFilename, { type: 'application/pdf' });
  }

  const mergedPdf = await PDFDocument.create();
  const totalFiles = files.length;

  for (let i = 0; i < totalFiles; i++) {
    onProgress?.({
      percent: Math.round((i / totalFiles) * 80),
      phase: i === 0 ? 'loading' : 'merging',
    });

    const arrayBuffer = await files[i].arrayBuffer();
    let sourcePdf: PDFDocument;
    try {
      sourcePdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    } catch (err) {
      throw new Error(
        `Failed to load "${files[i].name}": ${err instanceof Error ? err.message : 'corrupted or invalid PDF'}`,
      );
    }

    const pages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
    for (const page of pages) {
      mergedPdf.addPage(page);
    }
  }

  onProgress?.({ percent: 80, phase: 'saving' });

  const mergedBytes = await mergedPdf.save();

  onProgress?.({ percent: 100, phase: 'saving' });

  return new File([mergedBytes], outputFilename, { type: 'application/pdf' });
}
